import React, { useEffect, useState } from 'react';
import { TRUCK_NEEDS, TRUCK_TYPES, HAUL_TYPES, WATER_USES, TIME_SLOTS, truckNeedOf } from '../shopOrderTemplates';
import { jobLabel } from '../utils/ui';
import SearchSelect from '../components/SearchSelect';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path) { return fetch(`${API_BASE}${path}`, { headers: { 'x-worker-token': getToken() } }).then(r => r.ok ? r.json() : []); }

// "T-12 · 2019 Ford F-350" / "Big back trailer" — how a piece of iron reads in a picker.
export const assetLabel = e => `${e.asset_number ? e.asset_number + ' · ' : ''}${[e.year, e.make, e.model].filter(Boolean).join(' ') || e.name || ''}`.trim();
// Equipment, grouped so the likely picks come first: Construction Equipment, then everything else.
export function assetOptions(equipment, blank = '— Select equipment —') {
  const sorted = [...equipment].sort((a, b) =>
    (a.category === 'Construction Equipment' ? 0 : 1) - (b.category === 'Construction Equipment' ? 0 : 1)
    || String(a.asset_number || a.name || '').localeCompare(String(b.asset_number || b.name || ''), undefined, { numeric: true }));
  // `sub` is searched too (type a category or where it sits) and reads under the name.
  const sub = e => [e.category || null, e.current_location ? '📍 ' + e.current_location : null, e.name && !assetLabel(e).includes(e.name) ? e.name : null].filter(Boolean).join(' · ');
  return [{ value: '', label: blank }, ...sorted.map(e => ({ value: e.id, label: assetLabel(e), sub: sub(e) })), { value: '__custom', label: '+ Not listed — type it' }];
}

// Date + Morning / Afternoon / exact time. `date` is the order's needed_by; the time lives in
// details.needed_time ('AM' | 'PM' | 'HH:MM'). Shared by the truck request and the reservation.
export function WhenPicker({ date, onDate, time, onTime, mobile, label = 'When', inp, lbl, hint }) {
  const exact = !!time && time !== 'AM' && time !== 'PM';
  const chip = (on, extra = {}) => ({ padding: mobile ? '10px 14px' : '7px 12px', borderRadius: 999, border: `1.5px solid ${on ? '#b45309' : '#d1d5db'}`, background: on ? '#fffbeb' : '#fff', color: on ? '#92400e' : '#374151', fontSize: mobile ? 14 : 12.5, fontWeight: 700, cursor: 'pointer', ...extra });
  return (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={date || ''} onChange={e => onDate(e.target.value)} style={{ ...inp, width: mobile ? '100%' : 170 }} />
        {TIME_SLOTS.map(([k, l]) => <button key={k} type="button" onClick={() => onTime(time === k ? '' : k)} style={chip(time === k)}>{l}</button>)}
        <button type="button" onClick={() => onTime(exact ? '' : '09:00')} style={chip(exact)}>⏰ Time</button>
        {exact && <input type="time" value={time} onChange={e => onTime(e.target.value || '09:00')} style={{ ...inp, width: mobile ? 140 : 120 }} />}
      </div>
      {hint && <div style={hint}>Tomorrow at 9? Friday afternoon? Pick the day, then the morning, the afternoon or a time.</div>}
    </div>
  );
}

// "Request a Truck" — the requester says what they need (move a machine, haul material, a
// crane lift, water, something else) and when. The SHOP picks the truck, the driver and
// logs the hours on the order (Ryan, Oct 1 2026: "move Equipment and let the shop pick a
// truck"). Used on the driver's phone (/shop-request) and the office board.
//   details: the order's details object; onProject sets the order's job;
//   neededBy / onNeededBy: the order's date (rendered here, up top, with the time);
//   shopSide: show the shop's own box (truck type, driver, hours) — the office board.
// Which crane — the names the shop uses (Ryan, Oct 15 2026).
const CRANE_PICKS = ['Small Black Crane', 'Blue Crane', 'Big Crane', 'Digger'];

export default function RequestTruckFields({ details, setDetail, projectId, onProject, mobile, neededBy, onNeededBy, shopSide, onEquipment }) {
  const [equipment, setEquipment] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [approved, setApproved] = useState([]);

  useEffect(() => {
    apiFetch('/equipment').then(d => setEquipment(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/projects').then(d => setJobs(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const need = truckNeedOf(details);
  const isRental = e => !!e && (!!e.rental_vendor || e.category === 'Rented Equipment');
  const cranes = equipment.filter(e => Number(e.is_crane));

  useEffect(() => {
    if (projectId && need === 'haul') apiFetch(`/projects/${projectId}/approved-materials`).then(d => setApproved(Array.isArray(d) ? d : [])).catch(() => setApproved([]));
    else setApproved([]);
  }, [projectId, need]);

  const fontSize = mobile ? 16 : 13;
  const inp = { border: '1px solid #d1d5db', borderRadius: mobile ? 10 : 6, padding: mobile ? '12px' : '8px 10px', fontSize, width: '100%', boxSizing: 'border-box', background: '#fff' };
  const lbl = { fontSize: mobile ? 12 : 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' };
  const row = { marginBottom: mobile ? 12 : 10 };
  const hint = { fontSize: 11, color: '#9ca3af', marginTop: 4 };

  function pickNeed(k) {
    setDetail('need', k);
    // the truck type is the shop's call now — an old pick stays only where it still fits
    if (k === 'move_equipment' && details.truck_type && details.truck_type !== 'Transport') setDetail('truck_type', '');
  }
  function pickEquipment(id) {
    setDetail('equipment_id', id);
    if (onEquipment) onEquipment(id);
    const e = equipment.find(x => x.id === id);
    if (!e) return;
    setDetail('equipment_label', assetLabel(e));
    // Where it is now: its job, else (a rental never moved yet) the job its PO was for, else the yard.
    const fromJob = e.current_project_id || (!e.current_location && e.rental_po_project_id) || '';
    const j = fromJob && jobs.find(x => x.id === fromJob);
    setDetail('from_project_id', fromJob);
    setDetail('from_label', j ? jobLabel(j) : (e.current_location || 'Shop / Yard'));
    // A rental goes back where it came from (Pat, Oct 5 2026) — filled in, still changeable.
    if (isRental(e)) {
      setDetail('to_kind', 'vendor'); setDetail('to_project_id', '');
      setDetail('to_label', `Return to ${e.rental_vendor || 'the rental company'}${e.rental_po_number ? ` (PO ${e.rental_po_number})` : ''}`);
      if (onProject) onProject(fromJob);
    }
  }
  // From: a job, the yard, or somewhere typed in.
  function pickFrom(v) {
    if (v === '__yard') { setDetail('from_project_id', ''); setDetail('from_label', 'Shop / Yard'); return; }
    if (v === '__other') { setDetail('from_project_id', ''); setDetail('from_label', ''); setDetail('from_other', true); return; }
    const j = jobs.find(x => x.id === v);
    setDetail('from_other', false); setDetail('from_project_id', v || ''); setDetail('from_label', j ? jobLabel(j) : '');
  }
  // To (moves): a job, the yard, back to the rental company, or somewhere typed in.
  function pickTo(v) {
    const e = equipment.find(x => x.id === details.equipment_id);
    if (v === '__yard' || v === '__vendor' || v === '__other') {
      setDetail('to_kind', v.slice(2)); setDetail('to_project_id', '');
      setDetail('to_label', v === '__yard' ? 'Shop / Yard' : v === '__vendor' ? `Return to ${(e && e.rental_vendor) || 'the rental company'}${e && e.rental_po_number ? ` (PO ${e.rental_po_number})` : ''}` : '');
      if (onProject) onProject(details.from_project_id || '');
      return;
    }
    setDetail('to_kind', ''); pickJob(v);
  }
  function pickJob(id) {
    if (onProject) onProject(id);
    const j = jobs.find(x => x.id === id);
    setDetail('to_project_id', id); setDetail('to_label', j ? jobLabel(j) : '');
  }
  const jobPicker = (label, blank = '— Select job —') => (
    <div style={row}>
      <label style={lbl}>{label}</label>
      <SearchSelect value={projectId || ''} onChange={v => pickJob(v)} placeholder="Search jobs…" inputStyle={inp}
        options={[{ value: '', label: blank }, ...jobs.map(p => ({ value: p.id, label: jobLabel(p) }))]} />
    </div>
  );

  return (
    <div>
      {/* 1. What do you need? */}
      <div style={row}>
        <label style={lbl}>What do you need?</label>
        {/* Wraps to the window — the office board's window shrinks to a phone's width too, and five
            across ran off the side there (Ryan, Oct 14 2026). */}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {TRUCK_NEEDS.map(n => {
            const on = need === n.key;
            return (
              <button key={n.key} type="button" onClick={() => pickNeed(n.key)} title={n.hint}
                style={{ textAlign: 'left', padding: mobile ? '12px 12px' : '9px 10px', borderRadius: 10, border: `1.5px solid ${on ? '#b45309' : '#e5e7eb'}`, background: on ? '#fffbeb' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: mobile ? 22 : 18 }}>{n.icon}</span>
                <span style={{ fontSize: mobile ? 14 : 12.5, fontWeight: 700, color: on ? '#92400e' : '#111827', lineHeight: 1.15 }}>{n.label}</span>
              </button>
            );
          })}
        </div>
        {need && <div style={hint}>{(TRUCK_NEEDS.find(n => n.key === need) || {}).hint}</div>}
      </div>

      {/* 2. The details for that need */}
      {need === 'move_equipment' && (
        <>
          <div style={row}>
            <label style={lbl}>What&apos;s moving</label>
            <SearchSelect value={details.equipment_custom ? '__custom' : (details.equipment_id || '')}
              onChange={v => { if (v === '__custom') { setDetail('equipment_custom', true); setDetail('equipment_id', ''); if (onEquipment) onEquipment(''); } else { setDetail('equipment_custom', false); pickEquipment(v); } }}
              placeholder="Search by asset #, name or type…" inputStyle={inp} options={assetOptions(equipment)} />
          </div>
          {details.equipment_custom && (
            <div style={row}><label style={lbl}>Describe it</label><input value={details.custom_equipment || ''} onChange={e => setDetail('custom_equipment', e.target.value)} style={inp} placeholder="e.g. rented mini ex at 2631" /></div>
          )}
          <div style={row}>
            <label style={lbl}>From (where it is now)</label>
            <SearchSelect value={details.from_project_id || (details.from_other ? '__other' : details.from_label === 'Shop / Yard' ? '__yard' : '')} onChange={pickFrom} placeholder="Search jobs…" inputStyle={inp}
              options={[{ value: '', label: details.from_label && !details.from_project_id && details.from_label !== 'Shop / Yard' ? details.from_label : '— Pick where it is —' }, { value: '__yard', label: '🏠 Shop / Yard' }, { value: '__other', label: '✏️ Somewhere else (type it)' }, ...jobs.map(p => ({ value: p.id, label: jobLabel(p) }))]} />
            {details.from_other && <input value={details.from_label || ''} onChange={e => setDetail('from_label', e.target.value)} style={{ ...inp, marginTop: 6 }} placeholder="Address or place" />}
          </div>
          <div style={row}>
            <label style={lbl}>To</label>
            <SearchSelect value={details.to_project_id || (details.to_kind ? '__' + details.to_kind : '')} onChange={pickTo} placeholder="Search jobs…" inputStyle={inp}
              options={[{ value: '', label: '— Select job (or the yard) —' }, { value: '__yard', label: '🏠 Shop / Yard' },
                ...(isRental(equipment.find(x => x.id === details.equipment_id)) || details.to_kind === 'vendor' ? [{ value: '__vendor', label: details.to_kind === 'vendor' && details.to_label ? '↩ ' + details.to_label : '↩ Return to the rental company' }] : []),
                { value: '__other', label: '✏️ Somewhere else (type it)' }, ...jobs.map(p => ({ value: p.id, label: jobLabel(p) }))]} />
            {(details.to_kind === 'other' || details.to_kind === 'vendor') && <input value={details.to_label || ''} onChange={e => setDetail('to_label', e.target.value)} style={{ ...inp, marginTop: 6 }} placeholder={details.to_kind === 'vendor' ? 'Return to …' : 'Address or place'} />}
            {details.to_kind === 'vendor' && <div style={hint}>A rental — it goes back to where it was rented from. Add the branch address if the driver needs it.</div>}
          </div>
        </>
      )}

      {need === 'haul' && (
        <>
          {jobPicker('Job')}
          <div style={row}>
            <label style={lbl}>Haul</label>
            <select value={details.haul_type || ''} onChange={e => setDetail('haul_type', e.target.value)} style={inp}>
              <option value="">— Select —</option>
              {HAUL_TYPES.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          {(details.haul_type === 'Sand' || details.haul_type === 'Base') && (
            <div style={row}>
              <label style={lbl}>Material (approved)</label>
              <SearchSelect value={details.material_custom ? '__other' : (approved.includes(details.material) ? details.material : '')}
                onChange={v => { if (v === '__other') { setDetail('material_custom', true); setDetail('material', ''); } else { setDetail('material_custom', false); setDetail('material', v); } }}
                placeholder="Search materials…" inputStyle={inp}
                options={[{ value: '', label: '— Select approved material —' }, ...approved.map(m => ({ value: m, label: m })), { value: '__other', label: 'Other (type below)' }]} />
              {details.material_custom && <input value={details.material || ''} onChange={e => setDetail('material', e.target.value)} style={{ ...inp, marginTop: 6 }} placeholder="Material name" />}
              {approved.length === 0 && <div style={hint}>No approved submittal materials on this job — type it above.</div>}
            </div>
          )}
          {details.haul_type === 'AC' && <div style={row}><label style={lbl}>Mix / material</label><input value={details.material || ''} onChange={e => setDetail('material', e.target.value)} style={inp} /></div>}
          <div style={row}>
            <label style={lbl}>Quantity</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" value={details.qty || ''} onChange={e => setDetail('qty', e.target.value)} style={{ ...inp, flex: 1 }} />
              <select value={details.qty_unit || 'Yards'} onChange={e => setDetail('qty_unit', e.target.value)} style={{ ...inp, width: mobile ? 110 : 90 }}><option>Yards</option><option>Tons</option><option>Loads</option></select>
            </div>
          </div>
          {details.haul_type !== 'Off-haul' && <div style={row}><label style={lbl}>Pit / source (if you know)</label><input value={details.source || ''} onChange={e => setDetail('source', e.target.value)} style={inp} /></div>}
          {details.haul_type === 'Off-haul' && <div style={row}><label style={lbl}>Dump site (if you know)</label><input value={details.dump_site || ''} onChange={e => setDetail('dump_site', e.target.value)} style={inp} /></div>}
        </>
      )}

      {need === 'crane' && (
        <>
          {jobPicker('Job')}
          <div style={row}>
            <label style={lbl}>What&apos;s being lifted (and roughly how heavy)</label>
            <textarea value={details.lift_what || ''} onChange={e => setDetail('lift_what', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} placeholder="e.g. two 40' poles, set in augered holes" />
          </div>
        </>
      )}

      {need === 'water' && (
        <>
          {jobPicker('Job')}
          <div style={row}>
            <label style={lbl}>What for</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {WATER_USES.map(u => <button key={u} type="button" onClick={() => setDetail('water_for', u)} style={{ padding: mobile ? '10px 14px' : '7px 12px', borderRadius: 999, border: `1.5px solid ${details.water_for === u ? '#b45309' : '#d1d5db'}`, background: details.water_for === u ? '#fffbeb' : '#fff', color: details.water_for === u ? '#92400e' : '#374151', fontSize: mobile ? 14 : 12.5, fontWeight: 700, cursor: 'pointer' }}>{u}</button>)}
            </div>
          </div>
        </>
      )}

      {need === 'other' && (
        <>
          <div style={row}>
            <label style={lbl}>Describe what you need</label>
            <textarea value={details.what || ''} onChange={e => setDetail('what', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} placeholder="e.g. hook truck to pull the 20-yard bin at 2616" />
          </div>
          {jobPicker('Job (optional)', '— No job —')}
        </>
      )}

      {/* 3. When — right here, not buried at the bottom */}
      {need && (
        <div style={row}>
          <WhenPicker date={neededBy} onDate={onNeededBy} time={details.needed_time || ''} onTime={v => setDetail('needed_time', v)} mobile={mobile}
            label="When do you need it?" inp={inp} lbl={lbl} hint={hint} />
        </div>
      )}

      {/* 4. The shop's box — which truck, who drives, hours. Only on the office board. */}
      {shopSide && need && (
        <div style={{ ...row, marginTop: 6, padding: '10px 12px', borderRadius: 10, border: '1px dashed #b45309', background: '#fffdf7' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>🔧 Shop picks</div>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <div>
              <label style={lbl}>Truck</label>
              <select value={details.truck_type || ''} onChange={e => setDetail('truck_type', e.target.value)} style={inp}>
                <option value="">— Not picked yet —</option>
                {TRUCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {need === 'crane' && (
              <div>
                <label style={lbl}>Which crane</label>
                {/* The shop's own names for its cranes first (Ryan, Oct 15 2026), then any equipment
                    flagged as a crane. A named pick keeps crane_id empty and only sets crane_name. */}
                <SearchSelect value={details.crane_id || (details.crane_name && CRANE_PICKS.includes(details.crane_name) ? 'pick:' + details.crane_name : '')}
                  onChange={v => {
                    if (String(v).startsWith('pick:')) { setDetail('crane_id', ''); setDetail('crane_name', v.slice(5)); return; }
                    const c = cranes.find(x => x.id === v); setDetail('crane_id', v); setDetail('crane_name', c ? c.name : '');
                  }} placeholder="Search cranes…" inputStyle={inp}
                  options={[{ value: '', label: '— Not picked yet —' }, ...CRANE_PICKS.map(n => ({ value: 'pick:' + n, label: n })), ...cranes.map(c => ({ value: c.id, label: c.name }))]} />
              </div>
            )}
            <div><label style={lbl}>Driver</label><input value={details.driver || ''} onChange={e => setDetail('driver', e.target.value)} style={inp} placeholder="Who's taking it" /></div>
            <div><label style={lbl}>Hours</label><input type="number" step="0.5" value={details.hours || ''} onChange={e => setDetail('hours', e.target.value)} style={inp} /></div>
          </div>
          <div style={hint}>{need === 'move_equipment' ? 'Mark the order Complete when the move is done — the machine’s location updates and the hours go on its history.' : 'Hours are for billing — fill them in when the job is done.'}</div>
        </div>
      )}
    </div>
  );
}
